import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Link,
  Button,
  TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Chip
} from '@material-ui/core';
import { CheckCircle, ErrorOutline } from '@material-ui/icons';
import { exportToXlsx } from 'helpers/reportExport';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import TeamData from 'services/dataService/TeamData';
import { useSelector } from 'react-redux';

export default function SaveXls({exportData, errors = { invalidUsers: [], users: [] }, onSave}) {
  const [editableUsers, setEditableUsers] = useState([]);
  const [editingCell, setEditingCell] = useState({ row: null, field: null });
  const [validUsers, setValidUsers] = useState([]);  
  const [errorExportData, setErrorExportData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [teamUnvaliabe, setTeamUnvaliabe] = useState([]);

  const hasErrors = useMemo(() => {
    return editableUsers.length > 0 || 
            (validationErrors?.length > 0) || 
              (errorExportData?.data?.length > 0);
  }, [editableUsers, validationErrors, errorExportData]);

  // useEffect(() => {
  //   setEditableUsers(errors.invalidUsers || []);
  //   setValidUsers(errors.users || []);
  // }, [errors]);

  useEffect(() => {
    setEditableUsers(errors.invalidUsers || []);
    setValidUsers(prev => {
      if (prev.length === 0 && errors.users?.length) {
        return errors.users;
      }
      return prev;
    });
  }, [errors]);

  // useEffect(() => {
  //   setEditableUsers(errors.invalidUsers || []);
  //   setValidUsers(prev => errors.users?.length ? [...prev, ...errors.users] : prev);
  // }, [errors]);

  useEffect(() => {
    if (onSave) {
      onSave(validUsers);
    }
  }, [validUsers, onSave]);


  const totalValid = validUsers.length > 0 ? validUsers.length : errors?.users?.length || 0;

  const { t } = useTranslation(['label', 'button', 'form', 'files']);


  const { company } = useSelector((state) => state);
    
  const isEditing = (rowIdx, field) =>
    editingCell.row === rowIdx && editingCell.field === field;

  const handleStartEdit = (row, field) => {
    setEditingCell({ row, field });
  };

  const handleStopEdit = () => {
    setEditingCell({ row: null, field: null });
  };

  const exportActionType = {
    excel: (col, responseData, title) => {
      exportToXlsx(col, responseData, title);
    },
  };

  const handleDownload = () => {
    const extension =
      errors?.extension?.toLocaleLowerCase() === 'csv' ? 'csv' : 'excel';

    exportActionType[extension](
      [errorExportData ? errorExportData?.cols : exportData?.cols].map((c) => ({ field: c, title: c })),
      errorExportData ? errorExportData?.data : exportData.data,
      t('files:Csv_fields.invalid_users_filename')
    );
  };

  const handleFieldChange = (index, field, value) => {
    const newUsers = [...editableUsers];
    newUsers[index] = {
      ...newUsers[index],
      [field]: value,
    };
    setEditableUsers(newUsers);
  };

  const formatCSVResponse = (records) =>
    records.map(({ matchTeam, ...record }) => ({
      ...record,
      reject_reason: Array.isArray(record.reject_reason)
        ? record.reject_reason
            .map((reason) => {
              const [value, field] = reason.split(',');
              return t(`files:${value}`, { field });
            })
            .join('; ')
        : record.reject_reason,
    }));

    useEffect(() => {
      const splitTeam = formatCSVResponse(editableUsers);
        const regex = /O Time\s+"([^"]+)"\s+é inválido ou não existe/;
    
        const teamNames = splitTeam
          .map((r) => {
            const errorMessage = r?.reject_reason;
            if (typeof errorMessage !== 'string') return null;
    
            const match = errorMessage.match(regex);
            return match ? match[1] : null;
          })
          .filter(Boolean);
        
        setTeamUnvaliabe(teamNames)
    }, [editableUsers]);


    const handleCreateTeams = async () => {
      try {
        const splitTeam = formatCSVResponse(editableUsers);
        const regex = /O Time\s+"([^"]+)"\s+é inválido ou não existe/;
    
        const teamNames = splitTeam
          .map((r) => {
            const errorMessage = r?.reject_reason;
            if (typeof errorMessage !== 'string') return null;
    
            const match = errorMessage.match(regex);
            return match ? match[1] : null;
          })
          .filter(Boolean);
        
        setTeamUnvaliabe(teamNames)
        setShowConfirmation(true);    

      } catch (error) {
        console.error(error);
      }
    };

    const handleClickConfirmCreateTeam = async () => {
      try {
        const { data } = await TeamData.createMassiveTeam(
          teamUnvaliabe,
        );
        toast.success(t('toastify:Teams.attribuition_success'));
        setShowConfirmation(false);
      } catch (err) {
          if (err.response?.status === 403) {
            toast.error(t('toastify:commons.toast_error_403'));
          } else {
            toast.error(t('toastify:commons.toast_error_api'));
          }
        }
    };

    useEffect(() => {
      setEditableUsers(errors.invalidUsers || []);
      setValidUsers(prev => [...new Set([...(errors.users || []), ...prev])]);
    }, [errors]);

  const handleRevalidate = async () => {
    try {
      const response = await TeamData.revalidateMassiveUserTeams(
        editableUsers,
        company?.domain_company
      );

      const { users: newValidUsers, invalidUsers } = response.data;

      setValidationErrors(invalidUsers);
      setEditableUsers(Array.isArray(invalidUsers) ? invalidUsers : []);

      setValidUsers((prev) => {
        if (!prev.length && errors.users?.length) {
          return [...errors.users, ...newValidUsers];
        }
        return [...prev, ...newValidUsers];
      });


      if (invalidUsers?.length) {
        const formattedErrors = formatCSVResponse(invalidUsers);
        const cols = new Set(invalidUsers.flatMap((i) => Object.keys(i)));

        setErrorExportData({
          extension: 'excel',
          cols: [...cols],
          data: formattedErrors,
        });

        toast.error('Ainda há erros nos dados.');
      } else {
        setErrorExportData(null);
        setValidationErrors([]);
        toast.success('Todos os dados estão válidos!');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erro ao revalidar os dados.');
    }
  };
    
  return (
    <Box p={4}>
      <Grid container spacing={2} alignItems="center">
          <Grid item>
            {hasErrors && (
              <ErrorOutline style={{ color: '#d32f2f', fontSize: 40 }} />
            )}
          </Grid>
          <Grid item xs>
            {hasErrors && (
              <Typography variant="body1" color="error">
                Foram encontrados <strong>{editableUsers?.length}</strong> registro(s) com erro.
                {validUsers?.length > 0 && (
                  <>
                    <br />
                    <span style={{ color: '#2e7d32' }}>
                      <strong>{validUsers.length}</strong> registro(s) estão corretos e serão salvos.
                    </span>
                  </>
                )}
                <br />
                <Link onClick={handleDownload} style={{ cursor: 'pointer' }}>
                  Faça o download da planilha
                </Link>{' '}
                e verifique as inconsistências. Após isso, volte ao passo anterior e faça o upload novamente.
                <br />
                Você pode optar por editar os dados na tabela abaixo e clique em <strong>VALIDAR DADOS</strong>
              </Typography>
            )}
          </Grid>
        </Grid>
      {hasErrors ? (
        <>
          <Box mt={4}>
            <Paper elevation={3}>
              <Box p={2}>
                <Box
                  component="table"
                  width="100%"
                  style={{
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                    fontSize: 14,
                  }}
                >
                  <Box component="thead" bgcolor="#ffe6e6">
                    <Box component="tr">
                      {[
                        'Nome',
                        'E-mail',
                        'Cpf',
                        'Time',
                        'Erros',
                      ].map((header, idx) => (
                        <Box
                          key={idx}
                          component="th"
                          p={1}
                          align="left"
                          style={{ borderBottom: '1px solid #ccc' }}
                        >
                          {header}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {editableUsers.map((user, index) => (
                      <Box
                        component="tr"
                        key={index}
                        style={{ borderBottom: '1px solid #eee' }}
                      >
                        {/* Nome */}
                        <Box
                          component="td"
                          p={1}
                          width="15%"
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          onClick={() => handleStartEdit(index, 'name')}
                        >
                          {isEditing(index, 'name') ? (
                            <TextField
                              value={user.name || ''}
                              onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                              onBlur={handleStopEdit}
                              autoFocus
                              variant="outlined"
                              size="small"
                              InputProps={{
                                style: { fontSize: 14, padding: '6px 8px' },
                              }}
                            />
                          ) : (
                            <Typography variant="body1">{user.name}</Typography>
                          )}
                        </Box>

                        {/* Email */}
                        <Box
                          component="td"
                          p={1}
                          width="25%"
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          onClick={() => handleStartEdit(index, 'email')}
                        >
                          {isEditing(index, 'email') ? (
                            <TextField
                              value={user.email || ''}
                              onChange={(e) => handleFieldChange(index, 'email', e.target.value)}
                              onBlur={handleStopEdit}
                              autoFocus
                              variant="outlined"
                              size="small"
                              InputProps={{
                                style: { fontSize: 14, padding: '6px 8px' },
                              }}
                            />
                          ) : (
                            <Typography variant="body1">{user.email}</Typography>
                          )}
                        </Box>

                        {/* CPF */}
                        <Box
                          component="td"
                          p={1}
                          width="15%"
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          onClick={() => handleStartEdit(index, 'cpf')}
                        >
                          {isEditing(index, 'cpf') ? (
                            <TextField
                              value={user.cpf || ''}
                              onChange={(e) => handleFieldChange(index, 'cpf', e.target.value)}
                              onBlur={handleStopEdit}
                              autoFocus
                              variant="outlined"
                              size="small"
                              InputProps={{
                                style: { fontSize: 14, padding: '6px 8px' },
                              }}
                            />
                          ) : (
                            <Typography variant="body1">{user.cpf}</Typography>
                          )}
                        </Box>

                        {/* Time */}
                        <Box
                          component="td"
                          p={1}
                          width="15%"
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          onClick={() => handleStartEdit(index, 'team')}
                        >
                          {isEditing(index, 'team') ? (
                            <TextField
                              value={user.team || ''}
                              onChange={(e) => handleFieldChange(index, 'team', e.target.value)}
                              onBlur={handleStopEdit}
                              autoFocus
                              variant="outlined"
                              size="small"
                              InputProps={{
                                style: { fontSize: 14, padding: '6px 8px' },
                              }}
                            />
                          ) : (
                            <Typography variant="body1">{user.team}</Typography>
                          )}
                        </Box>

                        {/* Descrição do time */}
                        {/* <Box
                          component="td"
                          p={1}
                          width="20%"
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          <Typography variant="body1">
                            {user.matchTeam?.map((t, i) => (
                              <div key={i}>{i + 1}.&nbsp;{t.description}</div>
                            ))}
                          </Typography>
                        </Box> */}

                        {/* Erros */}
                        <Box
                          component="td"
                          p={1}
                          style={{ color: '#d32f2f', fontSize: 13 }}
                        >
                          {user.reject_reason.map((reason, i) => {
                            const [key, field] = reason.split(',');
                            return (
                              <div key={i}>
                                - {t(`files:${key}`, { field: field ?? '' })}
                              </div>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
          <Box style={{gap: 10}} display="flex" justifyContent="flex-end" mt={3}>
            {teamUnvaliabe.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateTeams}
              >
                Criar Times
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleRevalidate}
              disabled={editableUsers.length === 0}
            >
              Validar Dados
            </Button>
          </Box>
        </>
      ) : (
        <Grid style={{ marginTop: -35 }} container justifyContent="center" mt={5}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={4}
              style={{
                padding: 32,
                textAlign: 'center',
                backgroundColor: '#f1f8f4',
              }}
            >
              <CheckCircle
                style={{ fontSize: 60, color: '#4caf50', marginBottom: 16 }}
              />
              <Typography variant="h6" gutterBottom>
                Pronto para importar!
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Todos os dados foram validados com sucesso.
              </Typography>
              <Typography variant="h4" style={{ marginTop: 24, color: '#2e7d32' }}>
                {totalValid}{' '}
                {totalValid === 1 ? 'registro válido' : 'registros válidos'}
              </Typography>
              <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                Os dados estão prontos para serem importados. Clique em salvar que
                eles serão inseridos.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}
      <Dialog
          open={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Confirmação</DialogTitle>
          <DialogContent>
            <DialogContentText>
              O(s) time(s) abaixo não existe(m). Verifique o nome dos times corretos. Caso não exista, deseja criá-los para salvar os usuários?
            </DialogContentText>
            <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
              {[...new Set(teamUnvaliabe)]?.map((team, index) => (
                <Chip key={index} label={team} color="secondary" />
              ))}
            </Box>
            <br/>
            <DialogContentText>
              Após a criação dos times, clique novamente em <strong>VALIDAR DADOS</strong>. 😉
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setShowConfirmation(false)}>Cancelar</Button>
            <Button onClick={handleClickConfirmCreateTeam} color="primary" >
              Criar times
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
}
